import React, { useEffect, useState } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
  LuMapPin,
  LuClipboardList,
  LuMessageCircle,
  LuFileText,
  LuCalendarDays,
  LuMessageSquareWarning,
  LuCircleCheck,
  LuCircleX,
  LuLoader,
  LuArrowRight,
  LuReceipt,
} from "react-icons/lu";

const HomeEmployee = ({ setSelected, userid }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    checkedIn: false,
    checkinTime: null,
    pendingTasks: 0,
    unreadMessages: 0,
    openRequests: 0,
    todayShift: null,
  });

  useEffect(() => {
    if (selectedBuilding && userid) {
      loadDashboard();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding, userid]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const employeeId = String(userid);
      const buildingId = Number(selectedBuilding.buildingid);

      // Load attendance for today — filtered by EMPLOYEEID
      let checkedIn = false;
      let checkinTime = null;
      try {
        const attendanceQ = query(
          collection(db, "attendance"),
          where("buildingid", "==", buildingId),
          where("employeeid", "==", employeeId),
          where("date", "==", today)
        );
        const attendanceSnap = await getDocs(attendanceQ);
        attendanceSnap.forEach((doc) => {
          const data = doc.data();
          if (data.status === "checked_in") {
            checkedIn = true;
            checkinTime = data.checkinTime?.toDate?.()?.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" }) || null;
          }
        });
      } catch (e) {
        // Collection may not exist yet
      }

      // Load pending tasks count — filtered by EMPLOYEEID
      let pendingTasks = 0;
      try {
        const tasksQ = query(
          collection(db, "employee_tasks"),
          where("buildingid", "==", buildingId),
          where("employeeid", "==", employeeId),
          where("status", "in", ["pendiente", "en_progreso"])
        );
        const tasksSnap = await getDocs(tasksQ);
        pendingTasks = tasksSnap.size;
      } catch (e) {
        // Collection may not exist yet
      }

      // Load open requests count — filtered by EMPLOYEEID
      let openRequests = 0;
      try {
        const requestsQ = query(
          collection(db, "employee_requests"),
          where("buildingid", "==", buildingId),
          where("employeeid", "==", employeeId),
          where("status", "==", "pendiente")
        );
        const requestsSnap = await getDocs(requestsQ);
        openRequests = requestsSnap.size;
      } catch (e) {
        // Collection may not exist yet
      }

      // Sin KPI de pagos pendientes: el colaborador no ve finanzas del edificio
      // (las reglas no le dejan leer payments y el número quedaba siempre en 0).

      setStats({
        checkedIn,
        checkinTime,
        pendingTasks,
        unreadMessages: 0,
        openRequests,
        todayShift: null,
      });
    } catch (error) {
      console.error("Error loading employee dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
        <span style={styles.loadingText}>Cargando...</span>
      </div>
    );
  }

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuMessageSquareWarning size={48} color="#d97706" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
        <p style={styles.emptyText}>
          No tenés un edificio asignado. Contactá al administrador.
        </p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Greeting */}
      <div style={styles.greeting}>
        <h1 style={{ ...styles.greetingTitle, fontSize: isMobile ? 22 : 28 }}>
          {getGreeting()}, bienvenido
        </h1>
        <p style={styles.greetingSubtitle}>
          {selectedBuilding.name} &middot; {new Date().toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* Check-in Status Card */}
      <div style={{
        ...styles.checkinCard,
        borderLeft: `4px solid ${stats.checkedIn ? "#059669" : "#d97706"}`,
      }}>
        <div style={styles.checkinLeft}>
          <div style={{
            ...styles.checkinIcon,
            backgroundColor: stats.checkedIn ? "#ecfdf5" : "#fffbeb",
          }}>
            {stats.checkedIn ? (
              <LuCircleCheck size={24} color="#059669" />
            ) : (
              <LuCircleX size={24} color="#d97706" />
            )}
          </div>
          <div>
            <p style={styles.checkinStatus}>
              {stats.checkedIn ? "Asistencia registrada" : "No marcaste asistencia hoy"}
            </p>
            <p style={styles.checkinDetail}>
              {stats.checkedIn && stats.checkinTime
                ? `Entrada a las ${stats.checkinTime}`
                : "Recordá marcar tu entrada al llegar"}
            </p>
          </div>
        </div>
        <button
          style={styles.checkinButton}
          onClick={() => setSelected("checkin")}
        >
          <LuMapPin size={14} />
          {stats.checkedIn ? "Ver asistencia" : "Marcar asistencia"}
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ ...styles.kpiGrid, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : styles.kpiGrid.gridTemplateColumns, gap: isMobile ? 10 : 16, marginBottom: isMobile ? 20 : 32 }}>
        <KpiCard
          icon={LuClipboardList}
          label="Tareas pendientes"
          value={stats.pendingTasks}
          color="#6366f1"
          compact={isMobile}
          onClick={() => setSelected("tareasemployee")}
        />
        <KpiCard
          icon={LuMessageCircle}
          label="Mensajes"
          value={stats.unreadMessages}
          color="#0080ff"
          compact={isMobile}
          onClick={() => setSelected("chatemployee")}
        />
        <KpiCard
          icon={LuFileText}
          label="Trámites abiertos"
          value={stats.openRequests}
          color="#d97706"
          compact={isMobile}
          onClick={() => setSelected("solicitudes")}
        />
        <KpiCard
          icon={LuCalendarDays}
          label="Próximo turno"
          value={stats.todayShift || "—"}
          color="#059669"
          compact={isMobile}
          onClick={() => setSelected("turnos")}
        />
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        <h2 style={styles.sectionTitle}>Accesos rápidos</h2>
        <div style={styles.actionsGrid}>
          <QuickAction
            icon={LuMapPin}
            label="Marcar Asistencia"
            description="Registrar entrada o salida"
            onClick={() => setSelected("checkin")}
          />
          <QuickAction
            icon={LuClipboardList}
            label="Mis Tareas"
            description="Ver tareas asignadas"
            onClick={() => setSelected("tareasemployee")}
          />
          <QuickAction
            icon={LuMessageSquareWarning}
            label="Nuevo Reporte"
            description="Informar una novedad"
            onClick={() => setSelected("incidentesemployee")}
          />
          <QuickAction
            icon={LuFileText}
            label="Nuevo Trámite"
            description="Licencia, inasistencia, etc."
            onClick={() => setSelected("solicitudes")}
          />
          <QuickAction
            icon={LuMessageCircle}
            label="Chat"
            description="Mensajes con administración"
            onClick={() => setSelected("chatemployee")}
          />
          <QuickAction
            icon={LuReceipt}
            label="Liquidaciones"
            description="Ver liquidaciones de sueldo"
            onClick={() => setSelected("recibos")}
          />
        </div>
      </div>
    </div>
  );
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Buenos días";
  if (hour < 18) return "Buenas tardes";
  return "Buenas noches";
};

const KpiCard = ({ icon: Icon, label, value, color, onClick, compact }) => {
  if (compact) {
    return (
      <div
        style={{ ...styles.kpiCard, padding: 12, textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}
        onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      >
        <div style={{ ...styles.kpiIconWrapper, backgroundColor: `${color}15`, width: 34, height: 34, margin: 0, flexShrink: 0 }}>
          <Icon size={17} color={color} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...styles.kpiValue, fontSize: 20, marginBottom: 0, lineHeight: 1.1 }}>{value}</div>
          <div style={{ ...styles.kpiLabel, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        </div>
      </div>
    );
  }
  return (
    <div style={styles.kpiCard} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onClick?.()}>
      <div style={{ ...styles.kpiIconWrapper, backgroundColor: `${color}15` }}>
        <Icon size={20} color={color} />
      </div>
      <div style={styles.kpiValue}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, description, onClick }) => (
  <div
    style={styles.actionCard}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick?.()}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0080ff"; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.transform = "translateY(0)"; }}
  >
    <div style={styles.actionIcon}>
      <Icon size={20} color="#0080ff" />
    </div>
    <div style={styles.actionText}>
      <span style={styles.actionLabel}>{label}</span>
      <span style={styles.actionDesc}>{description}</span>
    </div>
    <LuArrowRight size={12} color="#6b7280" />
  </div>
);

const styles = {
  container: {
    padding: 24,
    maxWidth: 1200,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    color: "#4b5563",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  emptyText: {
    fontSize: 14,
    color: "#4b5563",
    margin: 0,
  },
  greeting: {
    marginBottom: 24,
  },
  greetingTitle: {
    fontSize: 28,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  greetingSubtitle: {
    fontSize: 14,
    color: "#4b5563",
    margin: "4px 0 0",
    textTransform: "capitalize",
  },
  checkinCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 24,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  checkinLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  checkinIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  checkinStatus: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  checkinDetail: {
    fontSize: 13,
    color: "#4b5563",
    margin: "2px 0 0",
  },
  checkinButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 16,
    marginBottom: 32,
  },
  kpiCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.15s ease",
    textAlign: "center",
  },
  kpiIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: 400,
    color: "#111827",
    marginBottom: 4,
  },
  kpiLabel: {
    fontSize: 12,
    color: "#6b7280",
    fontWeight: 400,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    marginBottom: 16,
  },
  quickActions: {
    marginBottom: 32,
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 12,
  },
  actionCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: "14px 16px",
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  actionIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#e6f2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  actionText: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  actionLabel: {
    fontSize: 14,
    fontWeight: 400,
    color: "#111827",
  },
  actionDesc: {
    fontSize: 12,
    color: "#6b7280",
  },
};

export default HomeEmployee;
